import styled from 'styled-components';
import { useDisplayWebContent } from '../../hooks/useDisplayWebContent';
import { ISelectOption, Input, Select } from '@salecycle/ui-component-library';
import { useCallback, useMemo } from 'react';
import { useEmbeddedSettingsSelector } from '../../state/selectors/useEmbeddedSettingsSelector';
import { useSetEmbeddedPosition } from '../../state/actions/useSetEmbeddedPosition';
import { useSetEmbeddedSelector } from '../../state/actions/useSetEmbeddedSelector';
import { TemplatePosition, TemplatePositionDescriptions } from 'entities/campaign/rulesets/template-position';
import { useFeatureFlags } from 'core/app/AppContext';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing4};
`;

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const EmbeddedTemplateControls = ({ id }: { id: string }) => {
  const { t } = useTranslation('campaigns');
  const { flagEnabled } = useFeatureFlags();
  const { getTemplate } = useDisplayWebContent();

  const setSelector = useSetEmbeddedSelector();
  const setPosition = useSetEmbeddedPosition();
  const { selector, position } = useEmbeddedSettingsSelector({ messageId: id });

  const selectOptions: ISelectOption<TemplatePosition>[] = useMemo(
    () => [
      { label: t(TemplatePositionDescriptions[TemplatePosition.beforebegin]), value: TemplatePosition.beforebegin },
      { label: t(TemplatePositionDescriptions[TemplatePosition.afterend]), value: TemplatePosition.afterend },
    ],
    [t],
  );

  const selectedPosition: ISelectOption<TemplatePosition> = useMemo(
    () => ({
      label: t(TemplatePositionDescriptions[position]),
      value: position,
    }),
    [position, t],
  );

  const template = getTemplate(id);

  const onSelectorChanged = useCallback(
    (value: string) => {
      setSelector({ id, selector: value });
    },
    [id, setSelector],
  );

  const onPositionChanged = useCallback(
    (selected: ISelectOption<TemplatePosition>) => {
      setPosition({ id, position: selected.value });
    },
    [id, setPosition],
  );

  if (!flagEnabled('ld-embedded-content') || !template?.isEmbedded) {
    return null;
  }

  return (
    <Container>
      <ControlsWrapper>
        <Input
          type="text"
          label={t('onsite.editor.workflow.displayWebContentMessage.drawer.embeddedContent.selectorLabel')}
          aria-label={t('onsite.editor.workflow.displayWebContentMessage.drawer.embeddedContent.selectorAria')}
          value={selector ?? ''}
          onChange={onSelectorChanged}
        />
        <Select
          label={t('onsite.editor.workflow.displayWebContentMessage.drawer.embeddedContent.positionLabel')}
          ariaLabel={t('onsite.editor.workflow.displayWebContentMessage.drawer.embeddedContent.positionAria')}
          value={selectedPosition}
          options={selectOptions}
          onChange={onPositionChanged}
        />
      </ControlsWrapper>
    </Container>
  );
};
